import { useCallback, useRef } from "react";
import { trackGtagEvent } from "../initGtag";

/**
 * Tracks time from the user's first interaction with the form until
 * you call recordSubmitSuccess() (e.g. after API success).
 * Use a stable form-specific identifier for formId.
 *
 * Spread formTrackingProps onto <form> (or FormSubmit / FormCard).
 */
export function useFormInteractionTime(formId) {
  const startedAtRef = useRef(null);

  const registerFirstInteraction = useCallback(() => {
    if (startedAtRef.current == null) {
      startedAtRef.current = performance.now();
    }
  }, []);

  /**
   * @param {object} [options]
   * @param {string} [options.form_id] — override default formId
   */
  const recordSubmitSuccess = useCallback(
    (options = {}) => {
      if (startedAtRef.current == null) return;
      const durationMs = Math.round(performance.now() - startedAtRef.current);
      startedAtRef.current = null;

      const resolvedId = options.form_id ?? formId;
      const payload = {
        form_id: resolvedId,
        duration_ms: durationMs,
        duration_sec: Math.round((durationMs / 1000) * 10) / 10,
      };
      trackGtagEvent("form_completion_time", payload);
    },
    [formId],
  );

  const reset = useCallback(() => {
    startedAtRef.current = null;
  }, []);

  const formTrackingProps = {
    onFocusCapture: registerFirstInteraction,
    onPointerDownCapture: registerFirstInteraction,
  };

  return { formTrackingProps, recordSubmitSuccess, reset };
}
